import type { IsoDate } from '../types/domain.js';
import { daysBetween, todayIso, toIsoDate } from '../dates/index.js';

/**
 * How long a job may go without running before the dashboard says so.
 *
 * The schedule is daily, so one miss is normal — GitHub delays or drops a
 * scheduled run when its runners are busy, and that is documented behaviour,
 * not a fault. Three days means two consecutive misses, which is not.
 */
export const SYNC_STALE_DAYS = 3;

export interface SyncRunLike {
  job: string;
  started_at: string;
}

export interface StaleJob {
  job: string;
  lastRunAt: string;
  daysAgo: number;
}

/**
 * Jobs whose newest run is older than the schedule should allow.
 *
 * A failed run is visible on its own: the row says "Hiba". A schedule that
 * stopped firing is not — the last successful run sits there, green, and the
 * page looks exactly like a healthy one. That is the failure mode of moving
 * the cron out of the database: **GitHub disables a repository's scheduled
 * workflows after 60 days with no push**, and this repo can easily go two
 * months without one. See D-24.
 *
 * Takes the runs the panel already fetched rather than querying again: the
 * newest run per job is the same list either way.
 */
export function staleSyncJobs(
  runs: readonly SyncRunLike[],
  now: Date = new Date(),
  afterDays: number = SYNC_STALE_DAYS,
): StaleJob[] {
  const today = todayIso(now);
  const newestPerJob = new Map<string, IsoDate>();

  for (const run of runs) {
    const day = toIsoDate(new Date(run.started_at));
    const seen = newestPerJob.get(run.job);
    if (seen === undefined || day > seen) newestPerJob.set(run.job, day);
  }

  return [...newestPerJob]
    .map(([job, lastRunAt]) => ({ job, lastRunAt, daysAgo: daysBetween(lastRunAt, today) }))
    .filter((entry) => entry.daysAgo > afterDays)
    .sort((a, b) => b.daysAgo - a.daysAgo);
}
